import { SUBSCRIPTION } from "~/lib/constants";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "~/components/ui/card";
import { PlanFeatureList } from "~/components/PlanFeatures";

interface DeepDivePricingCardProps {
  showPaidPlan: boolean;
  billingInterval: "month" | "year";
  onBillingIntervalChange: (interval: "month" | "year") => void;
  /** アクションボタン領域（View Plan Details / Subscribe Now 等） */
  action: React.ReactNode;
  /** "Current Plan" バッジを表示するか */
  showCurrentPlanBadge?: boolean;
  /** カードのボーダー色クラス（デフォルト: showPaidPlan に応じて自動決定） */
  borderClassName?: string;
}

export function DeepDivePricingCard({
  showPaidPlan,
  billingInterval,
  onBillingIntervalChange,
  action,
  showCurrentPlanBadge = false,
  borderClassName,
}: DeepDivePricingCardProps) {
  const border = borderClassName ?? (showPaidPlan ? "border-purple-500" : "border-gray-200");

  return (
    <Card className={`border-2 ${border} relative flex flex-col`}>
      <div className="absolute -top-5 left-1/2 transform -translate-x-1/2 whitespace-nowrap">
        {showPaidPlan ? (
          <div className="bg-gray-100 border border-gray-200 p-1 rounded-full flex items-center gap-1 shadow-sm">
            <button
              onClick={() => onBillingIntervalChange("month")}
              className={`px-4 py-1 rounded-full text-sm font-semibold transition-colors ${
                billingInterval === "month"
                  ? "bg-white text-gray-900 shadow-sm"
                  : "text-gray-500 hover:text-gray-700"
              }`}
            >
              Monthly
            </button>
            <button
              onClick={() => onBillingIntervalChange("year")}
              className={`px-4 py-1 rounded-full text-sm font-semibold transition-colors ${
                billingInterval === "year"
                  ? "bg-white text-gray-900 shadow-sm"
                  : "text-gray-500 hover:text-gray-700"
              }`}
            >
              Annual
              <span className="ml-1.5 text-xs bg-green-100 text-green-700 px-2 py-0.5 rounded-full">
                2 months free
              </span>
            </button>
          </div>
        ) : (
          <span className="bg-gray-400 text-white px-4 py-1 rounded-full text-sm font-semibold">
            Coming Soon
          </span>
        )}
      </div>
      <CardHeader>
        <div className="flex justify-between items-start mb-2">
          <CardTitle className="text-3xl">Deep Dive</CardTitle>
          {showCurrentPlanBadge && (
            <span className="text-xs bg-purple-500 text-white px-2 py-1 rounded">
              Current Plan
            </span>
          )}
        </div>
        <CardDescription className="text-2xl font-bold text-gray-900">
          {showPaidPlan ? (
            <>
              {billingInterval === "year" ? (
                <>
                  ${(SUBSCRIPTION.PLANS.PAID.PRICE_ANNUAL_USD as number).toFixed(2)}
                  <span className="text-base font-normal text-gray-500">/year</span>
                </>
              ) : (
                <>
                  ${SUBSCRIPTION.PLANS.PAID.PRICE_MONTHLY_USD}
                  <span className="text-base font-normal text-gray-500">/month</span>
                </>
              )}
              {/* Annual 表示時の高さに固定するため、Monthly 時は invisible で領域を確保 */}
              <div className={`text-sm font-normal mt-1 ${billingInterval !== "year" ? "invisible" : ""}`}>
                ${SUBSCRIPTION.PLANS.PAID.PRICE_ANNUAL_MONTHLY_USD}
                <span className="text-gray-500">/month — 2 months free</span>
              </div>
            </>
          ) : (
            <span className="text-gray-400">Coming Soon</span>
          )}
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-6 flex-1 flex flex-col">
        <div className="text-left flex-1">
          <PlanFeatureList planType="paid" />
        </div>
        {action}
      </CardContent>
    </Card>
  );
}
